/**
 * The pharmacy dashboard: what needs doing, and what the stock is worth.
 *
 * Counts only — each tile links to the screen where the work is done. Money
 * figures for the period live on Finance, which states its currency, dates
 * and location; this screen does not compete with it.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, ScanLine } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useLocationReady, useWorkingLocation } from '../../lib/locationContext';
import { money } from '../../lib/format';
import { Button, Card, CardHeader, ErrorState, Spinner, cn } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, type Overview } from './data';
import { Tile } from './parts';

export function PharmacyDashboardPage(): ReactNode {
  const { business, can } = useAuth();
  const { branchId, location } = useWorkingLocation();
  const ready = useLocationReady();
  const overview = usePharmacy<Overview>('/overview', { branchId: branchId ?? undefined }, ready && can('pharmacy:read'));
  const c = business?.currencyCode ?? 'NGN';

  if (!can('pharmacy:read')) {
    return (
      <div className="space-y-5">
        <PageHeader title="Pharmacy" description={business?.businessName ?? ''} />
        {can('pharmacy:sell') && (
          <Card>
            <CardHeader title="Ready to sell" subtitle="Open the till to start a sale." />
            <Link to="/pharmacy/pos">
              <Button icon={<ScanLine className="size-4" />}>Open POS</Button>
            </Link>
          </Card>
        )}
      </div>
    );
  }

  const o = overview.data;
  const work = o?.work ?? {};
  const urgent = (o?.alerts.expired ?? 0) + (work.open_recalls ?? 0) + (work.open_excursions ?? 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Pharmacy"
        description={location ? `${location.name}` : 'All locations'}
        action={
          can('pharmacy:sell') ? (
            <Link to="/pharmacy/pos">
              <Button icon={<ScanLine className="size-4" />}>Open POS</Button>
            </Link>
          ) : undefined
        }
      />

      {overview.isError ? (
        <ErrorState message="Could not load the pharmacy overview." onRetry={() => void overview.refetch()} />
      ) : !o ? (
        <Spinner label="Loading" />
      ) : (
        <>
          {urgent > 0 && (
            <div role="status" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden />
              <div>
                <p className="font-semibold">Needs attention</p>
                <p>
                  {[
                    o.alerts.expired && `${o.alerts.expired} expired batch${o.alerts.expired === 1 ? '' : 'es'} still on the shelf`,
                    work.open_recalls && `${work.open_recalls} open recall${work.open_recalls === 1 ? '' : 's'}`,
                    work.open_excursions && `${work.open_excursions} temperature excursion${work.open_excursions === 1 ? '' : 's'} awaiting a decision`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
          )}

          <section aria-label="Work waiting" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <WorkTile to="/pharmacy/prescriptions" label="Prescriptions to review" value={work.rx_awaiting_review ?? 0} />
            <WorkTile to="/pharmacy/prescriptions" label="Prescriptions to dispense" value={work.rx_to_dispense ?? 0} />
            <WorkTile to="/pharmacy/finance" label="Payments to confirm" value={work.pending_payments ?? 0} />
            <WorkTile to="/pharmacy/finance" label="Returns awaiting decision" value={work.returns_awaiting_decision ?? 0} />
            <WorkTile to="/pharmacy/stock/batches" label="Expired on shelf" value={o.alerts.expired} danger />
            <WorkTile to="/pharmacy/stock/batches" label="Expiring soon" value={o.alerts.nearExpiry} warn />
            <WorkTile to="/pharmacy/stock/batches" label="Low stock" value={o.alerts.lowStock} warn />
            <WorkTile to="/pharmacy/stock/adjustments" label="Adjustments to approve" value={o.alerts.pendingAdjustments} />
            <WorkTile to="/pharmacy/stock/adjustments" label="Quarantined or recalled" value={o.alerts.held} warn />
            <WorkTile to="/pharmacy/transfers" label="Transfers open" value={work.transfers_open ?? 0} />
            <WorkTile to="/pharmacy/stock/storage" label="Open excursions" value={work.open_excursions ?? 0} danger />
            <WorkTile to="/pharmacy/stock/recalls" label="Open recalls" value={work.open_recalls ?? 0} danger />
          </section>

          <Card>
            <CardHeader
              title="Stock at cost"
              subtitle="Every batch on hand at its own cost, plus stock in transit between locations"
            />
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Tile label="Saleable" value={money(o.valuation.saleable, c)} tone="good" />
              <Tile label="Quarantined / recalled" value={money(o.valuation.quarantinedOrRecalled, c)} tone="warn" />
              <Tile label="Expired" value={money(o.valuation.expired, c)} tone={Number(o.valuation.expired) > 0 ? 'bad' : 'plain'} />
              <Tile label="In transit" value={money(o.valuation.inTransit, c)} />
              <Tile label="Total" value={money(o.valuation.total, c)} />
            </div>
            {o.valuation.awaitingVerification && (
              <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
                <Tile
                  label={o.valuation.awaitingVerification.label}
                  value={money(o.valuation.awaitingVerification.value, c)}
                  sub={`${o.valuation.awaitingVerification.products} item(s), ${o.valuation.awaitingVerification.units} unit(s) · not for sale`}
                  tone="warn"
                />
                {o.valuation.units && (
                  <Tile
                    label="Units held"
                    value={o.valuation.units.recorded.toLocaleString()}
                    sub={`${o.valuation.units.onHand.toLocaleString()} with batches or not batch-tracked + ${o.valuation.units.countedPending.toLocaleString()} counted, batch/expiry pending`}
                  />
                )}
                <Tile label="Inventory incl. counted stock" value={money(o.valuation.totalIncludingAwaiting ?? o.valuation.total, c)} />
              </div>
            )}
            {o.valuation.uncostedItems > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                {o.valuation.uncostedItems} item(s) on hand have no cost recorded; they are counted, not valued.
              </p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function WorkTile({ to, label, value, warn, danger }: { to: string; label: string; value: number; warn?: boolean; danger?: boolean }): ReactNode {
  return (
    <Link
      to={to}
      className={cn(
        'group rounded-xl border bg-white p-3.5 transition-colors hover:border-brand-300',
        value > 0 && danger ? 'border-red-200' : value > 0 && warn ? 'border-amber-200' : 'border-slate-200',
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-1 flex items-center justify-between text-2xl font-semibold tabular-nums',
          value > 0 && danger ? 'text-red-700' : value > 0 && warn ? 'text-amber-700' : 'text-slate-900',
        )}
      >
        {value}
        <ArrowRight className="size-4 text-slate-300 group-hover:text-brand-600" aria-hidden />
      </p>
    </Link>
  );
}
